import React, { useState, useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Loader2, Lock, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import LoggedInHeader from './navigation/LoggedInHeader';
import { profileAPI } from '@/lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Premium routes that require subscription
// Maps route patterns to tool IDs for access checking
const PREMIUM_ROUTE_MAP = {
  // Get Hired (Premium)
  '/resume/builder': 'build_new_resume',
  '/resume/new': 'build_new_resume',
  '/resume/templates': 'browse_templates',
  '/templates': 'browse_templates',
  '/browse-templates': 'browse_templates',
  '/cover-letter': 'cover_letter',
  '/job-tracker': 'job_tracker',
  '/job-board-hub': 'job_board_hub',
  '/ats-check': 'ats_resume_check',
  '/wfh-directory': 'wfh_remote_directory',
  
  // Career Strategy (Premium)
  '/salary-analyzer': 'salary_analyzer',
  '/admin/credential-search': 'career_boost_finder',
  '/admin/skills-gap': 'skills_gap_analysis',
  '/resources': 'career_resources',
  '/dashboard/newsletter': 'newsletter_settings',
  
  // Learning (Premium)
  '/pathway-ged/learn-more': 'ged_learn_more',
  '/pathway-ged': 'ged_dashboard',
  
  // Professional Brand (Premium)
  '/admin/linkedin-optimizer': 'linkedin_optimizer',
  '/admin/networking-tracker': 'networking_tracker',
};

// Free routes that don't require premium access
const FREE_ROUTES = [
  '/resume/upload',
  '/jobs',
  '/interview-prep',
  '/assessments',
  '/career-planner',
  '/remote-work-center',
];

/**
 * PremiumRoute - Protects premium tool routes
 * Checks if user has premium access before allowing navigation
 * Redirects to pricing page if access is denied
 */
export default function PremiumRoute({ children, toolId }) {
  const location = useLocation();
  const [accessStatus, setAccessStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const performCheck = async () => {
      try {
        // First check if user is logged in
        const user = localStorage.getItem('user');
        const token = localStorage.getItem('access_token');
        
        if (!user) {
          setAccessStatus({ can_access: false, reason: 'not_authenticated' });
          setLoading(false);
          return;
        }
        
        // Parse user to check for admin role (admins always have access)
        try {
          const userData = JSON.parse(user);
          if (userData.roles?.includes('super_admin') || userData.roles?.includes('admin')) {
            setAccessStatus({ can_access: true, reason: 'admin_access' });
            setLoading(false);
            return;
          }
        } catch (e) {
          console.error('Error parsing user data:', e);
        }
        
        // Check if this route is free
        const currentPath = location.pathname;
        if (FREE_ROUTES.some(route => currentPath.startsWith(route))) {
          setAccessStatus({ can_access: true, reason: 'free_route' });
          setLoading(false);
          return;
        }
        
        // Determine tool ID from route or props
        const getToolIdFromRoute = (path) => {
          // Exact match first
          if (PREMIUM_ROUTE_MAP[path]) {
            return PREMIUM_ROUTE_MAP[path];
          }
          
          // Check for prefix match (for routes like /resume/builder/123)
          for (const [route, id] of Object.entries(PREMIUM_ROUTE_MAP)) {
            if (path.startsWith(route)) {
              return id;
            }
          }
          
          return null;
        };
        
        const detectedToolId = toolId || getToolIdFromRoute(currentPath);
        
        if (!detectedToolId) {
          // Route not in registry - allow access (might be admin route)
          setAccessStatus({ can_access: true, reason: 'route_not_registered' });
          setLoading(false);
          return;
        }
        
        // Check access via API (use credentials: 'include' for cookie-based auth)
        const headers = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        
        const response = await fetch(`${API_URL}/api/access/check/${detectedToolId}`, {
          credentials: 'include',
          headers
        });
        
        if (response.ok) {
          const data = await response.json();
          setAccessStatus(data);
        } else {
          // On error, default to allow (better UX than blocking)
          setAccessStatus({ can_access: true, reason: 'api_error' });
        }
      } catch (err) {
        console.error('Access check error:', err);
        setError(err.message);
        // On error, allow access (fail open for better UX)
        setAccessStatus({ can_access: true, reason: 'error' });
      } finally {
        setLoading(false);
      }
    };
    
    performCheck();
  }, [location.pathname, toolId]);

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-white">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Checking access...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (accessStatus?.reason === 'not_authenticated') {
    return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname)}`} replace />;
  }

  // Access denied - show upgrade prompt
  if (accessStatus && !accessStatus.can_access) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-white to-indigo-50 p-4">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
            <Lock className="w-10 h-10 text-white" />
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Premium Feature
          </h1>
          
          <p className="text-gray-600 mb-6">
            {accessStatus.tool_name 
              ? `${accessStatus.tool_name} is a premium feature.`
              : 'This feature requires a premium subscription.'
            }
            <br />
            Upgrade to unlock all premium tools and accelerate your career.
          </p>
          
          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6 text-left">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              Premium Benefits
            </h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-purple-500 rounded-full"></span>
                Unlimited resume downloads & templates
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-purple-500 rounded-full"></span>
                AI-powered career coaching
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-purple-500 rounded-full"></span>
                Cover letter generator
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-purple-500 rounded-full"></span>
                Job tracker & salary analyzer
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-purple-500 rounded-full"></span>
                LinkedIn optimizer & more
              </li>
            </ul>
          </div>
          
          <div className="space-y-3">
            {/* Trial Option - 3 days for $4 */}
            <Button 
              onClick={() => window.location.href = '/checkout?plan=monthly'}
              className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
              data-testid="start-trial-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Start 3-Day Trial for $4
            </Button>
            
            <Button 
              onClick={() => window.location.href = '/pricing'}
              variant="outline"
              className="w-full border-purple-200 hover:bg-purple-50"
              data-testid="upgrade-btn"
            >
              View All Plans
            </Button>
            
            <Button 
              variant="ghost"
              onClick={() => window.location.href = '/dashboard'}
              className="w-full text-gray-500"
            >
              Back to Dashboard
            </Button>
          </div>
          
          <p className="text-xs text-gray-400 mt-6">
            Trial: $4 for 3 days • Then $14.99/month • Cancel anytime
          </p>
        </div>
      </div>
    );
  }

  // Access granted - render children with AuthenticatedLayout
  return (
    <PremiumAuthenticatedLayout>
      {children}
    </PremiumAuthenticatedLayout>
  );
}

// AuthenticatedLayout wrapper for PremiumRoute - provides LoggedInHeader
const PremiumAuthenticatedLayout = ({ children }) => {
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  
  useEffect(() => {
    // Get user from localStorage first for immediate display
    const storedUser = localStorage.getItem('user');
    if (storedUser && storedUser !== 'undefined') {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        console.error('Failed to parse stored user:', e);
      }
    }
    
    // Fetch fresh user data
    const fetchUser = async () => {
      try {
        const response = await profileAPI.get();
        const userData = response.data.user || response.data;
        if (userData && userData.name) {
          setUser(userData);
          localStorage.setItem('user', JSON.stringify(userData));
        }
      } catch (error) {
        console.error('Failed to fetch user profile:', error);
      }
    };
    fetchUser();
  }, []);
  
  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };
  
  return (
    <div className="min-h-screen">
      <LoggedInHeader user={user} onLogout={handleLogout} />
      {children}
    </div>
  );
};

/**
 * Hook to check premium access status
 */
export function usePremiumAccess() {
  const [hasAccess, setHasAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userStatus, setUserStatus] = useState(null);

  useEffect(() => {
    const checkAccess = async () => {
      try {
        const user = localStorage.getItem('user');
        if (!user) {
          setHasAccess(false);
          setLoading(false);
          return;
        }
        
        // Check if admin via localStorage first
        try {
          const userData = JSON.parse(user);
          if (userData.roles?.includes('super_admin') || userData.roles?.includes('admin')) {
            setHasAccess(true);
            setUserStatus({ has_premium_access: true, reason: 'admin' });
            setLoading(false);
            return;
          }
        } catch (e) {
          // Continue with API check
        }

        const token = localStorage.getItem('access_token');
        const headers = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(`${API_URL}/api/access/status`, {
          credentials: 'include',
          headers
        });

        if (response.ok) {
          const data = await response.json();
          setUserStatus(data);
          setHasAccess(data.has_premium_access);
        }
      } catch (error) {
        console.error('Error checking premium access:', error);
      } finally {
        setLoading(false);
      }
    };

    checkAccess();
  }, []);

  return { hasAccess, loading, userStatus };
}
